// Shared "is this due date a problem?" classifier — the SINGLE source of truth
// for the overdue / due-soon logic that drives the row indicators on the Gag
// Breakdown gag rows AND the shared task board (list + kanban cards).
//
// Before this existed the overdue predicate was copy-pasted inline in
// TaskListView + TaskCard (with subtly different clause ordering). Import from
// here — never re-inline the comparison.

import { toLocalISO, formatDateWeekdayDayMonth } from './dateFormat';
import { addDays } from './ganttDates';

// A due date this many days ahead (inclusive, today counts as day 0) or sooner
// is "due soon". No prior constant existed for this threshold.
export const DUE_SOON_DAYS = 3;

// Statuses that mean the work is finished/abandoned — no overdue or due-soon
// styling applies. Matches the existing inline predicate (done | cancelled),
// which is shared by both the task status enum and the gag status enum.
const COMPLETE_STATUSES = ['done', 'cancelled'];

/**
 * Classify a due date + completion status into a due-state string:
 *  - 'overdue'  — due date strictly before today, item not complete → red family
 *  - 'due_soon' — due date today … today+DUE_SOON_DAYS inclusive, not complete → amber family
 *  - null       — no due date, item complete, or comfortably in the future
 *
 * @param {string|null|undefined} dueDate  YYYY-MM-DD (or ISO datetime — the
 *   date part is used); the gag's shoot_date or a task's due_date.
 * @param {string|null|undefined} status   the item's status enum value.
 * @param {Date} [today]  injectable "now" for testing; defaults to new Date().
 */
/**
 * THE due-date BUCKETS — My Tasks' "Group ▾ Due date" (Gags/teams/tasks Stage 1,
 * owner sign-off 2026-10-02: "Overdue · Today · This week · Later · No date").
 * In display order. `earlier` is the one bucket the spec does not name and is
 * DELIBERATE: a FINISHED task whose due date has passed is not overdue (this
 * file's own rule — `dueStatus` answers null for done/cancelled), and filing
 * it under Today/Later would put a past date in a future bucket.
 */
export const DUE_BUCKETS = Object.freeze([
  { id: 'overdue', label: 'Overdue' },
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This week' },
  { id: 'later', label: 'Later' },
  { id: 'none', label: 'No date' },
  { id: 'earlier', label: 'Earlier' },
]);

/**
 * Which `DUE_BUCKETS` id a due date falls in. "This week" = after today, up to
 * and including the coming SUNDAY (weeks start Monday, `ganttDates#toMonday`'s
 * convention). Local-date strings throughout (never a UTC slice).
 */
export function dueBucket(dueDate, status, today = new Date()) {
  if (!dueDate) return 'none';
  const due = String(dueDate).split('T')[0];
  const todayStr = toLocalISO(today);
  if (due < todayStr) return COMPLETE_STATUSES.includes(status) ? 'earlier' : 'overdue';
  if (due === todayStr) return 'today';
  const daysToSunday = (7 - today.getDay()) % 7; // getDay(): Sunday = 0
  if (due <= toLocalISO(addDays(today, daysToSunday))) return 'this_week';
  return 'later';
}

/**
 * THE shoot-DAY bucket — My Tasks' "View by Shoot date" (owner, 2026-10-02
 * header round): one group per day the GAG shoots, headed `TODAY` /
 * `TOMORROW` / `FRI 9 OCT` (his capitals), then `No shoot date` (a no-gag
 * task, or a gag with no date), then every past day together under `Shot`.
 * Returns `{ key, label, rank }`; sort groups by `rank`, then `key` (a
 * `YYYY-MM-DD` key sorts chronologically). Local-date strings throughout,
 * the same comparison `dueBucket` makes — never a UTC slice.
 */
export const SHOOT_DAY_NONE = 'none';
export const SHOOT_DAY_PAST = 'past';
export function shootDayBucket(shootDate, today = new Date()) {
  if (!shootDate) return { key: SHOOT_DAY_NONE, label: 'No shoot date', rank: 2 };
  const day = String(shootDate).split('T')[0];
  const todayStr = toLocalISO(today);
  if (day < todayStr) return { key: SHOOT_DAY_PAST, label: 'Shot', rank: 3 };
  if (day === todayStr) return { key: day, label: 'TODAY', rank: 1 };
  if (day === toLocalISO(addDays(today, 1))) return { key: day, label: 'TOMORROW', rank: 1 };
  return { key: day, label: formatDateWeekdayDayMonth(day).toUpperCase(), rank: 1 };
}

export function dueStatus(dueDate, status, today = new Date()) {
  if (!dueDate) return null;
  if (COMPLETE_STATUSES.includes(status)) return null;
  // Date-only comparison in local time — mirrors dateFormat.toLocalISO so we
  // never get the late-night UTC off-by-one the codebase guards against.
  const due = String(dueDate).split('T')[0];
  const todayStr = toLocalISO(today);
  if (due < todayStr) return 'overdue';
  if (due <= toLocalISO(addDays(today, DUE_SOON_DAYS))) return 'due_soon';
  return null;
}
